/**
 * Viral-Clip Finder — Submagic Magic Clips / OpusClip parity for /auto-captions.
 *
 * Problem this solves:
 *   For long videos (podcasts, lectures, sermons, gameplay commentary), the
 *   gold is buried inside 30+ minutes of context. Submagic + OpusClip + Vizard
 *   charge $30-100/mo to surface the 5-10 highest-virality 30-60 s segments.
 *   We want parity for free, fully in-browser.
 *
 * When the feature is "applicable":
 *   Only when video duration > 180 s. Anything shorter is already a clip and
 *   the UI clutters the page for no benefit. The blade reads isApplicable()
 *   before painting the card.
 *
 * Algorithm (deterministic, pure JS, no model):
 *   For each sliding window of 30 / 45 / 60 s width with 5 s stride:
 *     1. Prosody peak density — count words flagged emphasis:'high'|'med' by
 *        auto-captions-prosody.js, normalised per second. Higher = punchier
 *        delivery, the strongest single signal Submagic uses internally.
 *     2. Filler density — words matching common filler patterns (um/uh/like/
 *        you know / тип / короче / em/eh) per second. INVERSE-weighted: a
 *        clip with zero fillers reads as scripted/clean and ranks higher.
 *     3. Punctuation completeness — fraction of lines fully bounded by
 *        sentence-ending punctuation (. ! ? …). A clip starting mid-sentence
 *        kills retention.
 *     4. Speaker variety — number of distinct speakers in the window. Two-
 *        speaker debate clips out-perform monologue on TikTok / Reels.
 *     5. Pause-free runtime — fraction of window covered by speech (vs gaps
 *        > 0.6 s). Empty silence at the start = doom-scroll trigger.
 *
 *   Weighted score per window:
 *     score = 0.40 * prosodyDensity        (normalised to clip mean)
 *           + 0.15 * (1 - fillerDensity)   (clamped 0..1)
 *           + 0.20 * punctuationCompleteness
 *           + 0.10 * speakerVariety        (capped at 2 speakers)
 *           + 0.15 * pauseFreeRuntime
 *
 *   Overlap filter:
 *     We generate ~ duration/5 * 3 candidate windows. To avoid 8 near-
 *     identical results, keep only the top window per "region" (any two
 *     windows whose midpoints are within 30 s are the same region). The
 *     winning window in each region survives.
 *
 *   Final list:
 *     Sort surviving windows desc by score, return top 10. Ranking is
 *     stable across calls because all inputs are deterministic.
 *
 * Public API on window.__acClipFinder:
 *   .isApplicable()                            → bool (duration > 180 s)
 *   .findClips(words, lines, duration)         → [{ id, start, end, duration,
 *                                                   score, scoreLabel,
 *                                                   reasons, speakers,
 *                                                   preview }] (top 10)
 *   .buildClipURL(i)                           → '#clip-<id>' deeplink anchor
 *   .openClipInEditor(i)                       → seeks the video to the clip
 *                                                start, scrolls editor to the
 *                                                first matching line, dispatches
 *                                                __acExtractClip event for the
 *                                                burn pipeline / edit-by-
 *                                                transcript to consume.
 *   .reset()                                   → drop cached clips, hide card
 *
 * Burn integration:
 *   `openClipInEditor` dispatches CustomEvent('ac-extract-clip', { detail:
 *   { start, end, clipIndex } }) AND if window.__acExtractClip is a function,
 *   calls it directly. The pipeline can pick either path. Today the default
 *   handler (installed at the bottom of this file) uses edit-by-transcript:
 *   it marks every word OUTSIDE [start, end] as _deleted, so the next burn
 *   produces a video containing only the clip. Mirrors how OpusClip's
 *   "Export clip" button works.
 *
 * No-prosody fallback:
 *   When the prosody pass hasn't run yet (or didn't tag any word), we
 *   transparently substitute "word density" — words/second — as the
 *   prosody-peak signal. Long-form videos always have variable WPM so this
 *   still ranks meaningfully; results are flagged in `reasons` so the UI
 *   can hint "Run prosody for better ranking" if we want.
 */
(function(){
    'use strict';

    /* ────── tunables ────── */
    var MIN_DURATION_SEC = 180;          // gate for isApplicable()
    var WINDOW_WIDTHS = [30, 45, 60];    // candidate clip lengths (s)
    var STRIDE_SEC = 5;                  // sliding stride between candidates
    var TOP_N = 10;                      // max returned clips
    var REGION_RADIUS_SEC = 30;          // overlap-suppression radius
    var PAUSE_THRESHOLD_SEC = 0.6;       // gap above this = "pause"
    var MAX_SPEAKER_CAP = 2;             // saturate speaker bonus past 2

    /* Multi-language filler lexicon. Lower-cased, punctuation-stripped match.
       Kept terse on purpose — the score is robust to lexicon size; widening
       this list past ~40 entries per language gives diminishing returns. */
    var FILLERS = {
        en: ['um', 'uh', 'er', 'ah', 'like', 'yknow', 'youknow', 'i mean',
             'sort of', 'kind of', 'basically', 'actually', 'literally', 'so'],
        ru: ['э', 'эм', 'мм', 'ну', 'это', 'типа', 'короче', 'как бы',
             'вот', 'значит', 'там'],
        uk: ['е', 'ее', 'м', 'мм', 'ну', 'тобто', 'значить', 'коротше',
             'типу', 'ось', 'як би', 'знаєш', 'власне', 'взагалі'],
        tr: ['şey', 'yani', 'işte', 'aslında', 'hani', 'ıı', 'ee', 'tabii',
             'ya', 'ne bileyim', 'bir de', 'gibi'],
        de: ['äh', 'ähm', 'halt', 'also', 'so', 'eben', 'naja'],
        es: ['eh', 'em', 'este', 'osea', 'bueno', 'pues', 'tipo', 'como'],
        pt: ['ah', 'eh', 'tipo', 'tipo assim', 'né', 'então', 'pois é'],
        fr: ['euh', 'hum', 'bah', 'ben', 'genre', 'tu vois', 'enfin'],
        ko: ['음', '어', '그', '뭐', '아니', '그러니까', '약간'],
        id: ['eh', 'em', 'anu', 'gitu', 'kayak', 'gini', 'gimana ya'],
        ar: ['يعني', 'يَعني', 'هاي', 'إيه'],
    };

    /* Sentence-ending characters across the 9 locales. Includes CJK fullstops
       and the Arabic question mark. */
    var SENTENCE_END_RE = /[\.!\?…。．！？؟]$/;

    /* ────── state ────── */
    var _lastClips = [];
    var _resetAt = 0;

    /* ────── helpers ────── */

    function normalize(s){
        return String(s || '')
            .toLowerCase()
            .replace(/[\.,!\?;:'"«»„"\(\)\[\]…。．！？؟]/g, '')
            .trim();
    }

    function localeKey(){
        try {
            var loc = (window.__LOCALE || (document.documentElement.lang || 'en')).slice(0, 2);
            return FILLERS[loc] ? loc : 'en';
        } catch (e) { return 'en'; }
    }

    function fillerSet(){
        var lk = localeKey();
        var set = Object.create(null);
        var add = function(list){ if (list) for (var i = 0; i < list.length; i++) set[list[i]] = true; };
        add(FILLERS[lk]);
        // Always include English fillers — code-switched podcasts are common.
        if (lk !== 'en') add(FILLERS.en);
        return set;
    }

    /* Pick words whose midpoint falls inside [t0, t1). Half-open interval
       prevents the same word being double-counted across adjacent windows. */
    function wordsInWindow(words, t0, t1){
        var out = [];
        for (var i = 0; i < words.length; i++){
            var w = words[i];
            if (!w || typeof w.start !== 'number' || typeof w.end !== 'number') continue;
            var mid = (w.start + w.end) / 2;
            if (mid >= t0 && mid < t1) out.push(w);
        }
        return out;
    }

    /* Lines whose start falls inside the window. Used for punctuation
       completeness — we measure at the line granularity, not the word. */
    function linesInWindow(lines, t0, t1){
        var out = [];
        if (!Array.isArray(lines)) return out;
        for (var i = 0; i < lines.length; i++){
            var l = lines[i];
            if (!l || typeof l.start !== 'number') continue;
            if (l.start >= t0 && l.start < t1) out.push(l);
        }
        return out;
    }

    function lineText(line){
        if (!line) return '';
        if (typeof line.text === 'string') return line.text;
        if (Array.isArray(line.words)) {
            return line.words.map(function(w){ return (w && w.text) || ''; }).join(' ').trim();
        }
        return '';
    }

    /* ────── per-window features ────── */

    function prosodyDensity(winWords){
        if (!winWords.length) return 0;
        var hits = 0;
        var hasAnyEmphasisTag = false;
        for (var i = 0; i < winWords.length; i++){
            var e = winWords[i].emphasis;
            if (e === 'high') { hits += 1.0; hasAnyEmphasisTag = true; }
            else if (e === 'med') { hits += 0.5; hasAnyEmphasisTag = true; }
            else if (e === 'normal') { hasAnyEmphasisTag = true; }
        }
        // Fallback: prosody pass never ran → use word density (WPM/60) as proxy.
        if (!hasAnyEmphasisTag) {
            return winWords.length; // raw word count; normaliser handles scale
        }
        return hits;
    }

    function fillerDensity(winWords, fset){
        if (!winWords.length) return 0;
        var fillers = 0;
        for (var i = 0; i < winWords.length; i++){
            var t = normalize(winWords[i].text);
            if (t && fset[t]) fillers++;
        }
        return fillers / winWords.length; // 0..1
    }

    function punctuationCompleteness(winLines){
        if (!winLines.length) return 0;
        var complete = 0;
        for (var i = 0; i < winLines.length; i++){
            var txt = lineText(winLines[i]).trim();
            if (txt && SENTENCE_END_RE.test(txt)) complete++;
        }
        return complete / winLines.length;
    }

    function speakerVariety(winWords){
        var seen = Object.create(null);
        var n = 0;
        for (var i = 0; i < winWords.length; i++){
            var s = winWords[i].speaker;
            if (s == null) continue;
            if (!seen[s]) { seen[s] = true; n++; }
        }
        if (n === 0) return { count: 0, normalised: 0 };
        var capped = Math.min(n, MAX_SPEAKER_CAP);
        return { count: n, normalised: (capped - 1) / Math.max(1, MAX_SPEAKER_CAP - 1) };
    }

    function pauseFreeRuntime(winWords, t0, t1){
        var winLen = t1 - t0;
        if (winLen <= 0 || winWords.length < 2) return 0;
        // Speech runtime = sum of word durations; pauses inferred from gaps.
        var pauseTotal = 0;
        // Leading gap before first word
        var firstStart = Math.max(t0, winWords[0].start);
        if (firstStart - t0 > PAUSE_THRESHOLD_SEC) pauseTotal += (firstStart - t0);
        for (var i = 1; i < winWords.length; i++){
            var gap = winWords[i].start - winWords[i - 1].end;
            if (gap > PAUSE_THRESHOLD_SEC) pauseTotal += gap;
        }
        // Trailing gap after last word
        var lastEnd = Math.min(t1, winWords[winWords.length - 1].end);
        if (t1 - lastEnd > PAUSE_THRESHOLD_SEC) pauseTotal += (t1 - lastEnd);
        var ratio = 1 - (pauseTotal / winLen);
        if (ratio < 0) ratio = 0;
        if (ratio > 1) ratio = 1;
        return ratio;
    }

    /* ────── scoring ────── */

    /* Builds the unweighted feature row for every candidate window, then
       normalises prosody by the clip-mean before applying weights. Returns
       a list ready for overlap-filtering + sort. */
    function buildCandidates(words, lines, duration){
        var candidates = [];
        var fset = fillerSet();
        for (var w = 0; w < WINDOW_WIDTHS.length; w++){
            var width = WINDOW_WIDTHS[w];
            if (width >= duration) continue;
            for (var t0 = 0; t0 + width <= duration; t0 += STRIDE_SEC){
                var t1 = t0 + width;
                var winWords = wordsInWindow(words, t0, t1);
                if (!winWords.length) continue;
                var winLines = linesInWindow(lines, t0, t1);
                var sv = speakerVariety(winWords);
                candidates.push({
                    start: +t0.toFixed(2),
                    end: +t1.toFixed(2),
                    duration: width,
                    prosodyRaw: prosodyDensity(winWords),
                    fillerRatio: fillerDensity(winWords, fset),
                    puncComplete: punctuationCompleteness(winLines),
                    speakerCount: sv.count,
                    speakerNorm: sv.normalised,
                    pauseFree: pauseFreeRuntime(winWords, t0, t1),
                    wordCount: winWords.length,
                    firstLineText: winLines[0] ? lineText(winLines[0]).slice(0, 120) : '',
                });
            }
        }
        return candidates;
    }

    function normaliseAndScore(candidates){
        if (!candidates.length) return [];
        // Normalise prosody per candidate by the population mean so the unit
        // doesn't matter (works for both emphasis-weighted hits and raw word
        // counts in the fallback path).
        var sum = 0;
        for (var i = 0; i < candidates.length; i++) sum += candidates[i].prosodyRaw;
        var mean = sum / candidates.length;
        // Avoid /0 — every value normalised to 0 if every window had zero prosody.
        var denom = mean > 0 ? mean : 1;
        for (var j = 0; j < candidates.length; j++){
            var c = candidates[j];
            c.prosodyNorm = Math.min(1.5, c.prosodyRaw / denom); // cap at 1.5×mean
            var fillerInv = Math.max(0, 1 - c.fillerRatio * 4);  // 25 % fillers ⇒ 0
            c.score =
                0.40 * (c.prosodyNorm / 1.5) +     // bring back to 0..1
                0.15 * fillerInv +
                0.20 * c.puncComplete +
                0.10 * c.speakerNorm +
                0.15 * c.pauseFree;
            c.reasons = explainScore(c);
        }
        return candidates;
    }

    function explainScore(c){
        // Surface 1-3 strongest signals for the UI tooltip.
        var items = [
            { k: 'prosody', v: c.prosodyNorm / 1.5 },
            { k: 'low_filler', v: Math.max(0, 1 - c.fillerRatio * 4) },
            { k: 'complete_sentences', v: c.puncComplete },
            { k: 'two_speakers', v: c.speakerNorm },
            { k: 'flow', v: c.pauseFree },
        ];
        items.sort(function(a, b){ return b.v - a.v; });
        var out = [];
        for (var i = 0; i < items.length && i < 3; i++){
            if (items[i].v >= 0.3) out.push(items[i].k);
        }
        return out;
    }

    /* Region-based overlap filter: keep top-scoring window per region. */
    function dedupeOverlap(scored){
        var sorted = scored.slice().sort(function(a, b){ return b.score - a.score; });
        var kept = [];
        outer: for (var i = 0; i < sorted.length; i++){
            var s = sorted[i];
            var mid = (s.start + s.end) / 2;
            for (var k = 0; k < kept.length; k++){
                var keptMid = (kept[k].start + kept[k].end) / 2;
                if (Math.abs(mid - keptMid) < REGION_RADIUS_SEC) continue outer;
            }
            kept.push(s);
            if (kept.length >= TOP_N) break;
        }
        return kept;
    }

    /* ────── public API ────── */

    function isApplicable(){
        var st = window.__acState;
        if (!st) return false;
        var dur = (typeof st.duration === 'number' && st.duration > 0)
            ? st.duration
            : (st.file && st.file._duration) || 0;
        return dur > MIN_DURATION_SEC;
    }

    function findClips(words, lines, duration){
        words = Array.isArray(words) ? words : (window.__acState && window.__acState.words) || [];
        lines = Array.isArray(lines) ? lines : (window.__acState && window.__acState.lines) || [];
        if (!duration && window.__acState) duration = window.__acState.duration || 0;
        if (!duration || duration <= MIN_DURATION_SEC || !words.length) {
            _lastClips = [];
            return [];
        }
        var candidates = buildCandidates(words, lines, duration);
        var scored = normaliseAndScore(candidates);
        var top = dedupeOverlap(scored);
        // Final asc-by-start? No — keep score order so users see best-first.
        top.sort(function(a, b){ return b.score - a.score; });
        _lastClips = top.map(function(c, i){
            return {
                id: 'c' + (Date.now() % 1e6).toString(36) + '-' + i,
                start: c.start,
                end: c.end,
                duration: c.duration,
                score: +c.score.toFixed(3),
                scoreLabel: Math.round(c.score * 100) + '%',
                reasons: c.reasons,
                speakers: c.speakerCount,
                preview: c.firstLineText,
            };
        });
        return _lastClips.slice();
    }

    function buildClipURL(i){
        var c = _lastClips[i];
        if (!c) return '';
        return '#clip-' + c.id;
    }

    /* Seek the video, scroll editor to first matching line, dispatch event
       so any handler (burn pipeline, edit-by-transcript) can extract. */
    function openClipInEditor(i){
        var c = _lastClips[i];
        if (!c) return false;
        try {
            var video = document.getElementById('ac-preview-video') ||
                        document.querySelector('video');
            if (video) {
                try { video.currentTime = c.start; } catch(_){}
            }
            // Best-effort scroll the editor — find first line within window
            var editor = document.getElementById('ac-editor');
            if (editor && window.__acState && Array.isArray(window.__acState.lines)) {
                var lines = window.__acState.lines;
                var lineIdx = -1;
                for (var k = 0; k < lines.length; k++){
                    if (typeof lines[k].start === 'number' && lines[k].start >= c.start) {
                        lineIdx = k; break;
                    }
                }
                if (lineIdx >= 0) {
                    var lineEls = editor.querySelectorAll('.ac-ed-line');
                    var target = lineEls[lineIdx];
                    /* Use {block:'nearest'} per global rule — never jumps the
                       whole page, only scrolls if the line is off-screen. */
                    if (target && typeof target.scrollIntoView === 'function') {
                        target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                    }
                }
            }
        } catch (e) { /* never fail openClipInEditor on best-effort UI */ }

        // External handler: pipeline.js or any other module
        try {
            if (typeof window.__acExtractClip === 'function') {
                window.__acExtractClip({ start: c.start, end: c.end, clipIndex: i, clip: c });
            }
        } catch (e) {
            console.warn('[clip-finder] __acExtractClip handler threw', e);
        }
        // Event for loose listeners
        try {
            window.dispatchEvent(new CustomEvent('ac-extract-clip', {
                detail: { start: c.start, end: c.end, clipIndex: i, clip: c },
            }));
        } catch(_){}
        // Telemetry
        try {
            if (typeof window.__acReportFeature === 'function') {
                window.__acReportFeature('viral_clip_open', {
                    idx: i, score: c.score, duration: c.duration,
                });
            } else if (typeof window.__aeFeature === 'function') {
                window.__aeFeature('viral_clip_open', {
                    idx: i, score: c.score, duration: c.duration,
                });
            }
        } catch(_){}
        return true;
    }

    function reset(){
        _lastClips = [];
        _resetAt = Date.now();
        var card = document.getElementById('ac-clip-finder-card');
        if (card) card.hidden = true;
    }

    /* ────── UI: card with horizontal thumbnail strip ────── */

    function ensureCard(){
        var card = document.getElementById('ac-clip-finder-card');
        if (card) return card;
        // Mount above the export bar so the user sees clips before exporting.
        var anchor = document.getElementById('ac-export-burn') ||
                     document.getElementById('ac-editor') ||
                     document.body;
        if (!anchor || !anchor.parentNode) return null;
        card = document.createElement('section');
        card.id = 'ac-clip-finder-card';
        card.className = 'lg-card ac-clip-finder-card';
        card.hidden = true;
        card.setAttribute('role', 'region');
        card.setAttribute('aria-labelledby', 'ac-clip-finder-title');
        var i18n = window.__acI18n || {};
        card.innerHTML = [
            '<header class="ac-clip-finder-head">',
                '<h3 id="ac-clip-finder-title" class="ac-clip-finder-title">',
                    escapeHtml(i18n.viral_clip_title || 'Viral clips found'),
                '</h3>',
                '<p class="ac-clip-finder-sub">',
                    escapeHtml(i18n.viral_clip_subtitle ||
                        'Top moments ranked by emphasis, sentence completeness and flow.'),
                '</p>',
            '</header>',
            '<div class="ac-clip-finder-strip" id="ac-clip-finder-strip" role="list"></div>',
        ].join('');
        injectStyles();
        anchor.parentNode.insertBefore(card, anchor);
        return card;
    }

    function injectStyles(){
        if (document.getElementById('ac-clip-finder-styles')) return;
        var s = document.createElement('style');
        s.id = 'ac-clip-finder-styles';
        s.textContent = [
            '.ac-clip-finder-card{margin:16px 0;padding:16px;display:flex;flex-direction:column;gap:12px}',
            '.ac-clip-finder-title{font-size:16px;margin:0 0 4px;color:var(--text-primary,#fff)}',
            '.ac-clip-finder-sub{font-size:13px;color:var(--text-secondary,#aaa);margin:0}',
            '.ac-clip-finder-strip{display:flex;gap:12px;overflow-x:auto;padding-bottom:4px;scroll-snap-type:x mandatory}',
            '.ac-clip-thumb{flex:0 0 200px;scroll-snap-align:start;border:1px solid var(--border,#2a2a2a);background:var(--glass,rgba(255,255,255,0.04));border-radius:10px;padding:10px;display:flex;flex-direction:column;gap:6px}',
            '.ac-clip-thumb-head{display:flex;align-items:center;justify-content:space-between;gap:6px}',
            '.ac-clip-thumb-score{font-size:12px;font-weight:600;padding:2px 6px;border-radius:999px;background:rgba(99,179,237,0.18);color:#9bd}',
            '.ac-clip-thumb-dur{font-size:12px;color:var(--text-muted,#888);font-variant-numeric:tabular-nums}',
            '.ac-clip-thumb-preview{font-size:12px;line-height:1.35;color:var(--text-secondary,#aaa);min-height:36px;overflow:hidden;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical}',
            '.ac-clip-thumb-reasons{font-size:12px;color:var(--text-muted,#888);min-height:14px}',
            '.ac-clip-thumb-cut{font-size:12px;border:1px solid var(--accent,#3b82f6);background:transparent;color:var(--accent,#3b82f6);padding:6px 10px;border-radius:8px;cursor:pointer;margin-top:4px}',
            '.ac-clip-thumb-cut:hover{background:rgba(59,130,246,0.12)}',
            '.ac-clip-thumb-cut:focus-visible{outline:2px solid var(--accent,#3b82f6);outline-offset:2px}',
        ].join('\n');
        document.head.appendChild(s);
    }

    function escapeHtml(s){
        return String(s).replace(/[&<>"']/g, function(c){
            return ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' })[c];
        });
    }

    function fmtDur(d){
        var m = Math.floor(d / 60);
        var s = Math.round(d - m * 60);
        if (m < 1) return s + 's';
        return m + ':' + (s < 10 ? '0' + s : s);
    }

    function renderClips(clips){
        var card = ensureCard();
        if (!card) return;
        var strip = card.querySelector('#ac-clip-finder-strip');
        if (!strip) return;
        var i18n = window.__acI18n || {};
        var scoreLabel = i18n.viral_clip_score || 'Score';
        var btnLabel = i18n.viral_clip_cut_button || 'Cut this clip';
        if (!clips || !clips.length) {
            card.hidden = true;
            strip.innerHTML = '';
            return;
        }
        var parts = [];
        for (var i = 0; i < clips.length; i++){
            var c = clips[i];
            parts.push('<article class="ac-clip-thumb" role="listitem" data-clip-idx="' + i + '">');
            parts.push('<div class="ac-clip-thumb-head">');
            parts.push('<span class="ac-clip-thumb-score" title="' + escapeHtml(scoreLabel) + '">' + escapeHtml(c.scoreLabel) + '</span>');
            parts.push('<span class="ac-clip-thumb-dur">' + escapeHtml(fmtDur(c.duration)) + '</span>');
            parts.push('</div>');
            parts.push('<div class="ac-clip-thumb-preview">' + escapeHtml(c.preview || '…') + '</div>');
            parts.push('<div class="ac-clip-thumb-reasons">' + escapeHtml((c.reasons || []).join(' · ')) + '</div>');
            parts.push('<button type="button" class="ac-clip-thumb-cut" data-clip-idx="' + i + '">' + escapeHtml(btnLabel) + '</button>');
            parts.push('</article>');
        }
        strip.innerHTML = parts.join('');
        card.hidden = false;
        // Delegate click — single listener.
        if (!strip.__bound) {
            strip.addEventListener('click', function(e){
                var btn = e.target.closest && e.target.closest('.ac-clip-thumb-cut');
                if (!btn) return;
                var idx = parseInt(btn.getAttribute('data-clip-idx'), 10);
                if (!isNaN(idx)) openClipInEditor(idx);
            });
            strip.__bound = true;
        }
    }

    /* Default __acExtractClip handler — uses edit-by-transcript to mark every
       word OUTSIDE the clip as _deleted, so the next burn produces the clip
       only. Pipeline can override by setting its own window.__acExtractClip
       BEFORE this script loads (or replacing it after, since we don't lock). */
    function defaultExtractHandler(detail){
        var ebt = window.__acEditByTranscript;
        if (!ebt || typeof ebt.deleteWord !== 'function') return;
        var st = window.__acState;
        if (!st || !Array.isArray(st.words)) return;
        // Reset prior state — extracting a clip is a full re-deletion.
        if (typeof ebt.reset === 'function') ebt.reset();
        var t0 = detail.start, t1 = detail.end;
        for (var i = 0; i < st.words.length; i++){
            var w = st.words[i];
            if (!w || typeof w.start !== 'number') continue;
            // Word ends before clip OR starts after clip → outside the clip
            var outside = (w.end <= t0) || (w.start >= t1);
            if (outside) ebt.deleteWord(i);
        }
        try {
            if (typeof window.__acToast === 'function') {
                var i18n = window.__acI18n || {};
                window.__acToast(i18n.viral_clip_cut_button || 'Clip extracted', 'success');
            }
        } catch(_){}
    }

    /* ────── auto-discovery: render after captions finish ────── */

    function tryAutoRender(){
        if (!isApplicable()) return;
        var st = window.__acState;
        if (!st || !st.words || !st.words.length) return;
        var clips = findClips(st.words, st.lines || [], st.duration || 0);
        renderClips(clips);
    }

    function bindLifecycle(){
        // Captions newly rendered → recompute.
        window.addEventListener('ac-captions-ready', tryAutoRender);
        // Prosody finished → recompute (better signals).
        window.addEventListener('ac-prosody-applied', tryAutoRender);
        // Cleaner removed ranges → recompute against shorter timeline.
        window.addEventListener('ac-cleaner-applied', tryAutoRender);
        // Fallback poll for late state arrival (state injected by pipeline.js
        // after defer — 3 retries, stops once we render or after 6 s).
        var tries = 0;
        var poll = setInterval(function(){
            if (++tries > 12) { clearInterval(poll); return; }
            if (_lastClips.length) { clearInterval(poll); return; }
            tryAutoRender();
        }, 500);
    }

    /* ────── boot ────── */

    window.__acClipFinder = {
        isApplicable: isApplicable,
        findClips: findClips,
        buildClipURL: buildClipURL,
        openClipInEditor: openClipInEditor,
        reset: reset,
        // Exposed for tests + integration:
        _internals: {
            MIN_DURATION_SEC: MIN_DURATION_SEC,
            WINDOW_WIDTHS: WINDOW_WIDTHS,
            STRIDE_SEC: STRIDE_SEC,
            buildCandidates: buildCandidates,
            normaliseAndScore: normaliseAndScore,
            dedupeOverlap: dedupeOverlap,
            renderClips: renderClips,
        },
    };

    // Only install the default extract handler if no one else claimed it first.
    if (typeof window.__acExtractClip !== 'function') {
        window.__acExtractClip = defaultExtractHandler;
    }

    function boot(){
        bindLifecycle();
        tryAutoRender();
    }
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
    } else {
        boot();
    }
})();
